import React, { useState, useEffect, useRef, useCallback } from "react";
import DashboardLayout from "../../UI/Layout/DashboardLayout";
import Title from "../../UI/Text/Title";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import api from "../../../axios/Axios";
import Axios from "../../../axios/Axios";
import { CAMPAIGN_REPORT } from "../../../constants/ServiceConstants";
import { HOST_URL_DB2, NODE_HOST_URL, NODE_HOST_URL_v1 } from "../api/Api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch, faTimes, faChevronLeft, faChevronRight,
  faSpinner, faMobileAlt, faClock, faCheckCircle,
  faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import JSONPretty from "react-json-pretty";
import Dialog from "react-dialog";

const STATUS_CONFIG = {
  delivered: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  failed: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  sent: { color: "#d97706", bg: "#fef3c7", border: "#fde68a" },
  read: { color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  delivered_to_network: { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0" },
  default: { color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" },
};

const getStatusStyle = (status) =>
  STATUS_CONFIG[status ? status.toLowerCase() : ""] || STATUS_CONFIG.default;

const CampaignReport = () => {
  const lStorage = JSON.parse(localStorage.getItem("login"));
  const


    userId = lStorage.user.userId;
  const userToken = lStorage.token;
  const username = lStorage.user.username

  const { id, channel, campaignName, totalData } = useParams();

  const perPageRef = useRef(10);
  const allSearchResultsRef = useRef([]);
  const totalDataCount = parseInt(totalData, 10) || 0;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Dialog for WhatsApp API log details
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedApiLog, setSelectedApiLog] = useState(null);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const isMobile = windowWidth < 640;

  // ── WhatsApp (server-side page_no) ───────────────────────────────────────
  const fetchWhatsApp = useCallback(async (page) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(CAMPAIGN_REPORT, {
        token: userToken,
        user_id: userId,
        campaign_id: id,
        method: "report",
        channel: "whatsapp",
        page_no: page,
      });
      if (data.success === true) {
        const records = Array.isArray(data.data) ? data.data : [];
        if (page === 1 && records.length > 0) {
          perPageRef.current = records.length;
          setPerPage(records.length);
        }
        const pp = perPageRef.current;
        const pages = totalDataCount > 0 && pp > 0 ? Math.ceil(totalDataCount / pp) : 1;
        setReportData(records);
        setCurrentPage(page);
        setTotalPages(Math.max(1, pages));
      } else {
        setError(data.message || "Failed to load data.");
      }
    } catch (err) {
      setError(err.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [userId, userToken, id, totalDataCount]);

  // ── SMS (server-side page_no) ─────────────────────────────────────────────
  const fetchSms = useCallback(async (page) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${HOST_URL_DB2}/bulk_campaign_sms.php?user_id=${userId}&method=report&token=${userToken}&channel=sms&campaign_id=${id}&page_no=${page}`
      );
      const result = await response.json();
      if (result.success === true) {
        const records = Array.isArray(result.data) ? result.data : [];
        if (page === 1 && records.length > 0) {
          perPageRef.current = records.length;
          setPerPage(records.length);
        }
        const pp = perPageRef.current;
        const pages = totalDataCount > 0 && pp > 0 ? Math.ceil(totalDataCount / pp) : 1;
        setReportData(records);
        setCurrentPage(page);
        setTotalPages(Math.max(1, pages));
      } else {
        setError(result.message || "Failed to load data.");
      }
    } catch (err) {
      setError(err.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [userId, userToken, id, totalDataCount]);

  // ── RCS (server-side, totalPages from API) ────────────────────────────────
  const fetchRcs = useCallback(async (page) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await Axios.post(`${NODE_HOST_URL_v1}/rcs`, {
        token: userToken,
        user_id: userId,
        campaign_id: id,
        method: "campaign_report_dtl",
        page: page,
      });
      if (data && data.data) {
        const records = Array.isArray(data.data) ? data.data : [];
        if (page === 1 && records.length > 0) {
          perPageRef.current = records.length;
          setPerPage(records.length);
        }
        const pp = perPageRef.current;
        const apiPages = parseInt(data.totalPages, 10) || 1;
        const pages = totalDataCount > 0 && pp > 0 ? Math.ceil(totalDataCount / pp) : apiPages;
        setReportData(records);
        setCurrentPage(page);
        setTotalPages(Math.max(1, pages));
      } else {
        setError("Failed to load data.");
      }
    } catch (err) {
      setError(err.message || "Network error.");
    } finally {
      setLoading(false);
    }
  }, [userId, userToken, id, totalDataCount]);

  useEffect(() => {
    if (channel === "whatsapp") fetchWhatsApp(1);
    else if (channel === "sms") fetchSms(1);
    else if (channel === "rcs") fetchRcs(1);
  }, [channel, fetchWhatsApp, fetchSms, fetchRcs]);

  // ── Page change ───────────────────────────────────────────────────────────
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    if (searchQuery) {
      const pp = perPageRef.current || 10;
      const start = (page - 1) * pp;
      setReportData(allSearchResultsRef.current.slice(start, start + pp));
      setCurrentPage(page);
      return;
    }
    if (channel === "whatsapp") fetchWhatsApp(page);
    else if (channel === "sms") fetchSms(page);
    else if (channel === "rcs") fetchRcs(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  // const handleSearch = async () => {
  //   if (!searchInput.trim()) return;
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     if (channel === "whatsapp") {
  //       const { data } = await api.post(CAMPAIGN_REPORT, {
  //         token: userToken,
  //         user_id: userId,
  //         method: "filter",
  //         campaign_id: id,
  //         mobile: searchInput,
  //       });
  //       if (data.success === true) {
  //         setReportData(Array.isArray(data.data) ? data.data : []);
  //         setSearchQuery(searchInput.trim());
  //       } else {
  //         setError(data.message || "No results found.");
  //       }
  //     } else if (channel === "sms") {
  //       const { data } = await Axios.post(`${NODE_HOST_URL}/sms`, {
  //         token: userToken,
  //         user_id: userId,
  //         method: "filter_camp_data",
  //         campaign_id: id,
  //         search: searchInput,
  //       });
  //       if (data.success === true) {
  //         setReportData(Array.isArray(data.data) ? data.data : []);
  //         setSearchQuery(searchInput.trim());
  //       } else {
  //         setError(data.message || "No results found.");
  //       }
  //     } else if (channel === "rcs") {
  //       const { data } = await Axios.post(`${NODE_HOST_URL}/rcs`, {
  //         token: userToken,
  //         user_id: userId,
  //         method: "filter_camp_data",
  //         campaign_id: id,
  //         search: searchInput,
  //       });
  //       if (data.success === true) {
  //         setReportData(Array.isArray(data.data) ? data.data : []);
  //         setSearchQuery(searchInput.trim());
  //       } else {
  //         setError(data.message || "No results found.");
  //       }
  //     }
  //   } catch (err) {
  //     setError(err.message || "Search failed.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const SearchNumber = async () => {
    setError(null);

    try {
      const paramData = {
        user_id: userId,
        method: "CampaignSearch",
        number: channel === "whatsapp"
          ? parseInt(searchInput)
          : searchInput,
        campaign_id: channel === "rcs"
          ? id
          : parseInt(id),
        username: username,
        filter:
          channel === "whatsapp"
            ? "wpSearch"
            : channel === "sms"
              ? "smsSearch"
              : "rcsSearch",
        token: userToken,
      };

      const { data } = await Axios.post(
        `${NODE_HOST_URL_v1}/UserCampaignSearch`,
        paramData,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );

      if (data.success) {
        const results = Array.isArray(data.data) ? data.data : [];
        const pp = perPageRef.current || 10;
        allSearchResultsRef.current = results;
        setTotalPages(Math.max(1, Math.ceil(results.length / pp)));
        setCurrentPage(1);
        setReportData(results.slice(0, pp));
        setSearchQuery(searchInput.trim());
      } else {
        setError(data.message || "Search failed. Please try again.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Network error during search."
      );
    }
  };

  const resetSearch = () => {
    setSearchInput("");
    setSearchQuery("");
    setError(null);
    allSearchResultsRef.current = [];
    if (channel === "whatsapp") {
      fetchWhatsApp(1);
    } else if (channel === "sms") {
      fetchSms(1);
    } else if (channel === "rcs") {
      fetchRcs(1);
    }
  };

  // ── Page pills ────────────────────────────────────────────────────────────
  const renderPagePills = () => {
    if (totalPages <= 1) return null;
    const pills = Array.from({ length: totalPages }, (_, i) => i + 1)
      .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
      .reduce((acc, p, idx, arr) => {
        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
        acc.push(p);
        return acc;
      }, []);

    return pills.map((p, idx) =>
      p === "..." ? (
        <span key={"e" + idx} style={{ padding: "0 6px", color: "#94a3b8", fontSize: "13px" }}>…</span>
      ) : (
        <button
          key={p}
          onClick={() => handlePageChange(p)}
          style={{
            minWidth: "34px", height: "34px",
            border: "1px solid",
            borderColor: currentPage === p ? "#4f46e5" : "#e2e8f0",
            background: currentPage === p ? "#4f46e5" : "#fff",
            color: currentPage === p ? "#fff" : "#475569",
            borderRadius: "8px", fontSize: "13px",
            fontWeight: currentPage === p ? "700" : "400",
            cursor: "pointer",
          }}
        >
          {p}
        </button>
      )
    );
  };

  // ── Channel badge colour ──────────────────────────────────────────────────
  const channelBadge = {
    whatsapp: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
    sms: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
    rcs: { bg: "#fdf4ff", color: "#9333ea", border: "#e9d5ff" },
  };
  const badge = channelBadge[channel] || channelBadge.sms;

  // ── Column headers per channel ────────────────────────────────────────────
  const COLUMNS = {
    whatsapp: ["Mobile Number", "Status", "DLR Response", "Parameters", "Sent Time", "Deliver Time", "Read Time"],
    sms: ["Mobile Number", "Content", "Status", "Submitted On", "Delivered On", "Count", "Error Code"],
    rcs: ["Phone Number", "Country", "Campaign Message", "Sent Time", "Delivery Time", "Status", "Error Code", "Reason"],
  };

  const TABLE_MIN_WIDTH = { whatsapp: "900px", sms: "820px", rcs: "1020px" };

  // ── Row renderer per channel ──────────────────────────────────────────────
  const renderRow = (x, i) => {
    const sts = getStatusStyle(x.status);
    const rowStyle = {
      borderBottom: "1px solid #f1f5f9",
      transition: "background 0.15s",
    };
    const tdBase = { padding: "13px 16px" };
    const pill = {
      background: sts.bg, color: sts.color,
      border: "1px solid " + sts.border,
      padding: "3px 10px", borderRadius: "100px",
      fontSize: "11px", fontWeight: "700",
      textTransform: "capitalize", whiteSpace: "nowrap",
    };
    const clamp = {
      fontSize: "12px", color: "#475569",
      display: "-webkit-box", WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical", overflow: "hidden", wordBreak: "break-word",
    };

    if (channel === "whatsapp") {
      return (
        <tr key={i} style={rowStyle}
          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FontAwesomeIcon icon={faMobileAlt} style={{ color: "#6366f1", fontSize: "13px" }} />
              <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>{x.mobile || "—"}</span>
            </div>
          </td>
          <td style={tdBase}>
            <span style={pill}>{x.status || "—"}</span>
            <FontAwesomeIcon
              icon={faExclamationCircle}
              style={{ marginLeft: "8px", color: "#6366f1", fontSize: "12px", cursor: "pointer" }}
              onClick={() => {
                const log = x.api_log_id && x.api_log_id.trim() !== ""
                  ? { api_log_id: x.api_log_id }
                  : { api_status: x.api_status };
                setSelectedApiLog(log);
                setDialogOpen(true);
              }}
            />
          </td>
          <td style={{ ...tdBase, maxWidth: "240px" }}>
            <span style={clamp}>
              {(x.status === "failed" && !x.dlr_reponse) ? x.api_log_id : (x.dlr_reponse || "—")}
            </span>
          </td>
          <td style={{ ...tdBase, maxWidth: "200px" }}>
            <div style={{ fontSize: "11px", color: "#475569", overflow: "hidden", maxHeight: "56px" }}>
              <JSONPretty id={"jp" + i} data={x.parameter} style={{ fontSize: "11px" }} />
            </div>
          </td>
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FontAwesomeIcon icon={faClock} style={{ color: "#94a3b8", fontSize: "11px" }} />
              <span style={{ fontSize: "12px", color: "#475569" }}>{x.created_date || "—"}</span>
            </div>
          </td>
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FontAwesomeIcon
                icon={x.deliver_time ? faCheckCircle : faClock}
                style={{ color: x.deliver_time ? "#16a34a" : "#cbd5e1", fontSize: "11px" }}
              />
              <span style={{ fontSize: "12px", color: "#475569" }}>{x.deliver_time || "—"}</span>
            </div>
          </td>
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FontAwesomeIcon
                icon={x.read_time ? faCheckCircle : faClock}
                style={{ color: x.read_time ? "#16a34a" : "#cbd5e1", fontSize: "11px" }}
              />
              <span style={{ fontSize: "12px", color: "#475569" }}>{x.read_time || "—"}</span>
            </div>
          </td>
        </tr>
      );
    }

    if (channel === "sms") {
      return (
        <tr key={i} style={rowStyle}
          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FontAwesomeIcon icon={faMobileAlt} style={{ color: "#6366f1", fontSize: "13px" }} />
              <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>{x.mobile || x.phone_number || "—"}</span>
            </div>
          </td>
          <td style={{ ...tdBase, maxWidth: "200px" }}>
            <span style={clamp}>{x.sms_content || x.content || "—"}</span>
          </td>
          <td style={tdBase}>
            <span style={pill}>{x.status || "—"}</span>
          </td>
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FontAwesomeIcon icon={faClock} style={{ color: "#94a3b8", fontSize: "11px" }} />
              <span style={{ fontSize: "12px", color: "#475569" }}>{x.submit_time || x.sent_time || "—"}</span>
            </div>
          </td>
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FontAwesomeIcon
                icon={(x.deliver_time || x.delivered_time) ? faCheckCircle : faClock}
                style={{ color: (x.deliver_time || x.delivered_time) ? "#16a34a" : "#cbd5e1", fontSize: "11px" }}
              />
              <span style={{ fontSize: "12px", color: "#475569" }}>{x.deliver_time || x.delivered_time || "—"}</span>
            </div>
          </td>
          <td style={{ ...tdBase, textAlign: "center", fontSize: "12px", color: "#475569" }}>
            {x.msg_count || x.count || "—"}
          </td>
          <td style={{ ...tdBase, maxWidth: "160px" }}>
            <span style={clamp}>{x.api_status || x.error_code || "—"}</span>
          </td>
        </tr>
      );
    }

    if (channel === "rcs") {
      return (
        <tr key={i} style={rowStyle}
          onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <FontAwesomeIcon icon={faMobileAlt} style={{ color: "#6366f1", fontSize: "13px" }} />
              <span style={{ fontSize: "13px", fontWeight: "600", color: "#334155" }}>{x.phone_number || "—"}</span>
            </div>
          </td>
          <td style={{ ...tdBase, fontSize: "12px", color: "#475569" }}>{x.country_code || "—"}</td>
          <td style={{ ...tdBase, maxWidth: "200px" }}>
            <span style={clamp}>{x.campaign_message || x.parameters || "—"}</span>
          </td>
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FontAwesomeIcon icon={faClock} style={{ color: "#94a3b8", fontSize: "11px" }} />
              <span style={{ fontSize: "12px", color: "#475569" }}>{x.sent_time || "—"}</span>
            </div>
          </td>
          <td style={tdBase}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <FontAwesomeIcon
                icon={x.deliver_time ? faCheckCircle : faClock}
                style={{ color: x.deliver_time ? "#16a34a" : "#cbd5e1", fontSize: "11px" }}
              />
              <span style={{ fontSize: "12px", color: "#475569" }}>{x.deliver_time || "—"}</span>
            </div>
          </td>
          <td style={tdBase}>
            <span style={pill}>{x.status || "—"}</span>
          </td>
          <td style={{ ...tdBase, maxWidth: "160px" }}>
            <span style={clamp}>{x.api_status || "—"}</span>
          </td>
          <td style={{ ...tdBase, maxWidth: "160px" }}>
            <span style={clamp}>{x.api_response || "—"}</span>
          </td>
        </tr>
      );
    }

    return null;
  };

  const isNextDisabled = currentPage >= totalPages;

  const showPagination = !loading && reportData.length > 0;

  return (
    <DashboardLayout>
      <main className="content">
        <div className="container-fluid p-0">
          <Title isButton={false}>Campaign Reports</Title>

          {/* Error banner */}
          {error && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "12px",
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: "12px", padding: "14px 18px", marginBottom: "16px",
            }}>
              <span style={{ fontSize: "18px", lineHeight: 1 }}>&#9888;</span>
              <div>
                <div style={{ fontSize: "14px", fontWeight: "700", color: "#dc2626", marginBottom: "2px" }}>Something went wrong</div>
                <div style={{ fontSize: "13px", color: "#b91c1c" }}>{error}</div>
              </div>
              <button
                onClick={() => setError(null)}
                style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#f87171", fontSize: "16px", lineHeight: 1, padding: 0 }}
              >
                &#x2715;
              </button>
            </div>
          )}

          {/* Main card */}
          <div style={{
            background: "#fff", borderRadius: "16px",
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            fontFamily: "Inter, system-ui, sans-serif",
            overflow: "hidden",
          }}>

            {/* ── Header ── */}
            <div style={{
              padding: "20px 24px", borderBottom: "1px solid #f1f5f9",
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: "12px",
            }}>
              <div>
                <h3 style={{ margin: 0, fontSize: isMobile ? "14px" : "17px", fontWeight: "800", color: "#0f172a" }}>
                  {campaignName && (
                    <span style={{ color: "#4f46e5" }}>{decodeURIComponent(campaignName)}</span>
                  )}
                  <span style={{ color: "#94a3b8", fontWeight: "400", fontSize: "13px", marginLeft: "8px" }}>
                    / {id}
                  </span>
                </h3>
                <span style={{
                  display: "inline-block", marginTop: "6px",
                  padding: "2px 10px", borderRadius: "100px",
                  fontSize: "11px", fontWeight: "700",
                  background: badge.bg, color: badge.color,
                  border: "1px solid " + badge.border,
                  textTransform: "uppercase", letterSpacing: "0.5px",
                }}>
                  {channel}
                </span>
              </div>

              {/* Search */}
              <div style={{ display: "flex", alignItems: "center", gap: "8px", width: isMobile ? "100%" : "auto" }}>
                <div style={{ position: "relative", flex: isMobile ? 1 : "none" }}>
                  <FontAwesomeIcon
                    icon={faSearch}
                    style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: "12px", pointerEvents: "none" }}
                  />
                  <input
                    type="text"
                    placeholder="Search mobile number…"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") SearchNumber(); }}
                    style={{
                      paddingLeft: "30px", paddingRight: searchInput ? "30px" : "12px",
                      height: "36px", border: "1px solid #e2e8f0", borderRadius: "8px",
                      fontSize: "13px", color: "#334155", outline: "none",
                      width: isMobile ? "100%" : "220px", background: "#f8fafc",
                      boxSizing: "border-box",
                    }}
                  />
                  {searchInput && (
                    <button
                      onClick={resetSearch}
                      style={{ position: "absolute", right: "8px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, color: "#94a3b8", fontSize: "11px" }}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  )}
                </div>
                <button
                  onClick={SearchNumber}
                  style={{
                    height: "36px", padding: isMobile ? "0 12px" : "0 16px",
                    background: "#4f46e5", color: "#fff",
                    border: "none", borderRadius: "8px",
                    fontSize: "13px", fontWeight: "600", cursor: "pointer",
                    display: "flex", alignItems: "center", gap: "6px",
                    flexShrink: 0,
                  }}
                >
                  <FontAwesomeIcon icon={faSearch} style={{ fontSize: "11px" }} />
                  {!isMobile && " Search"}
                </button>
              </div>
            </div>

            {/* ── Table ── */}
            {loading ? (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "80px 0", flexDirection: "column", gap: "12px" }}>
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "28px", color: "#4f46e5" }} />
                <span style={{ color: "#64748b", fontSize: "13px" }}>Loading records…</span>
              </div>
            ) : reportData.length === 0 ? (
              <div style={{ textAlign: "center", padding: "80px 0", color: "#94a3b8" }}>
                <FontAwesomeIcon icon={faMobileAlt} style={{ fontSize: "36px", marginBottom: "12px", opacity: 0.4 }} />
                <p style={{ margin: 0, fontSize: "14px" }}>No records found.</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: TABLE_MIN_WIDTH[channel] || "900px" }}>
                  <thead style={{ background: "#f8fafc" }}>
                    <tr style={{ borderBottom: "1px solid #e2e8f0" }}>
                      {(COLUMNS[channel] || []).map((h, i) => (
                        <th key={i} style={{
                          padding: "12px 16px", fontSize: "11px", fontWeight: "700",
                          color: "#64748b", textTransform: "uppercase", letterSpacing: "0.5px",
                          textAlign: "left", whiteSpace: "nowrap",
                        }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((x, i) => renderRow(x, i))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Pagination ── */}
            {showPagination && (
              <div style={{
                padding: "16px 24px", borderTop: "1px solid #f1f5f9",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: isMobile ? "stretch" : "center",
                justifyContent: "space-between",
                gap: "10px",
              }}>
                {/* Info */}
                <div>
                  {searchQuery ? (
                    <span style={{ fontSize: "13px", color: "#334155", fontWeight: "600" }}>
                      Showing{" "}
                      <span style={{ color: "#4f46e5" }}>
                        {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, allSearchResultsRef.current.length)}
                      </span>{" "}
                      of <span style={{ color: "#4f46e5" }}>{allSearchResultsRef.current.length}</span> results
                    </span>
                  ) : totalDataCount > 0 ? (
                    <span style={{ fontSize: "13px", color: "#334155", fontWeight: "600" }}>
                      Showing{" "}
                      <span style={{ color: "#4f46e5" }}>
                        {(currentPage - 1) * perPage + 1}–{Math.min(currentPage * perPage, totalDataCount)}
                      </span>{" "}
                      of <span style={{ color: "#4f46e5" }}>{totalDataCount}</span> records
                    </span>
                  ) : (
                    <span style={{ fontSize: "13px", color: "#334155", fontWeight: "600" }}>
                      Page <span style={{ color: "#4f46e5" }}>{currentPage}</span>
                      {totalPages > 1 && (
                        <span style={{ color: "#334155" }}> of <span style={{ color: "#4f46e5" }}>{totalPages}</span></span>
                      )}
                    </span>
                  )}
                </div>

                {/* Controls */}
                <div style={{ display: "flex", gap: "6px", alignItems: "center", justifyContent: isMobile ? "space-between" : "flex-end" }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{
                      height: "34px", padding: "0 14px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: currentPage === 1 ? "#f8fafc" : "#fff",
                      color: currentPage === 1 ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} style={{ fontSize: "10px" }} /> Prev
                  </button>

                  {isMobile ? (
                    <span style={{ fontSize: "13px", color: "#475569", fontWeight: "600" }}>
                      {currentPage} / {totalPages}
                    </span>
                  ) : (
                    renderPagePills()
                  )}

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={isNextDisabled}
                    style={{
                      height: "34px", padding: "0 14px",
                      border: "1px solid #e2e8f0", borderRadius: "8px",
                      background: isNextDisabled ? "#f8fafc" : "#fff",
                      color: isNextDisabled ? "#cbd5e1" : "#4f46e5",
                      fontSize: "13px", fontWeight: "600",
                      cursor: isNextDisabled ? "not-allowed" : "pointer",
                      display: "flex", alignItems: "center", gap: "5px",
                    }}
                  >
                    Next <FontAwesomeIcon icon={faChevronRight} style={{ fontSize: "10px" }} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* API Log Dialog — WhatsApp only */}
          {dialogOpen && (
            <Dialog
              modal={true}
              title={selectedApiLog && selectedApiLog.api_log_id ? "API Log Details" : "API Status"}
              isDraggable={true}
              onClose={() => { setDialogOpen(false); setSelectedApiLog(null); }}
            >
              <div style={{ width: "450px", fontSize: "14px", padding: "8px" }}>
                <JSONPretty id="json-dialog" data={selectedApiLog} style={{ fontSize: "14px" }} />
              </div>
            </Dialog>
          )}
        </div>
      </main>
    </DashboardLayout>
  );
};

export default CampaignReport;
